/**
 * Biometric Authentication Service
 *
 * Face ID / Touch ID / fingerprint via `@aparajita/capacitor-biometric-auth`,
 * with the saved email+password kept in `@aparajita/capacitor-secure-storage`
 * (Keychain on iOS, Keystore on Android).
 *
 * ⚠️ THIS NEVER WORKED ON iOS BEFORE 2026-07-31. It previously used
 * `capacitor-native-biometric`, which ships a CocoaPods podspec but **no
 * `Package.swift`**. This app is SPM (`ios/App/CapApp-SPM`, no Podfile), so
 * `npx cap sync` warned "capacitor-native-biometric does not have a
 * Package.swift" and omitted it — the plugin was never in the iOS binary and
 * every call rejected as unimplemented. Because availability turned any
 * exception into `isAvailable: false`, the UI just hid the button and nobody
 * noticed. Both replacement plugins ship `Package.swift` and target Capacitor 8.
 *
 * That failure shape is why this file now distinguishes "biometry is genuinely
 * unavailable on this device" from "the plugin is broken" — see `BiometricStatus`.
 *
 * @module BiometricService
 */

import {
  BiometricAuth,
  BiometryError,
  BiometryErrorType,
  BiometryType,
} from '@aparajita/capacitor-biometric-auth';
import { SecureStorage } from '@aparajita/capacitor-secure-storage';
import { isNative } from '@/utils/platform';

export { BiometryType, BiometryErrorType };

/** Must not collide with the session credential key (`tradeloop.session.id`). */
const CREDENTIALS_KEY = 'tradeloop.biometric.credentials';

export interface Credentials {
  username: string;
  password: string;
}

/**
 * Why biometric login is or is not offered. `pluginUnavailable` is deliberately
 * NOT folded into `unavailable`: the first means this app is misconfigured, the
 * second means this device cannot do biometrics. Conflating them is exactly what
 * let a missing native plugin hide indefinitely.
 */
export type BiometricStatus =
  | { available: true; biometryType: BiometryType; name: string }
  | {
      available: false;
      reason: 'unavailable' | 'notEnrolled' | 'pluginUnavailable';
      message: string;
    };

export interface BiometricAvailability {
  isAvailable: boolean;
  biometryType: BiometryType;
  errorMessage?: string;
  /** Set only when the plugin itself failed, i.e. an integration bug. */
  pluginError?: string;
}

/** Human-readable name for a biometry type. */
export function getBiometryTypeName(type: BiometryType): string {
  switch (type) {
    case BiometryType.touchId:
      return 'Touch ID';
    case BiometryType.faceId:
      return 'Face ID';
    case BiometryType.fingerprintAuthentication:
      return 'Fingerprint';
    case BiometryType.faceAuthentication:
      return 'Face Authentication';
    case BiometryType.irisAuthentication:
      return 'Iris Authentication';
    default:
      return 'Biometric';
  }
}

/** Structured availability. Never throws. */
export async function getBiometricStatus(): Promise<BiometricStatus> {
  if (!isNative) {
    return { available: false, reason: 'unavailable', message: 'Not a native platform' };
  }

  try {
    const info = await BiometricAuth.checkBiometry();

    if (info.isAvailable) {
      return {
        available: true,
        biometryType: info.biometryType,
        name: getBiometryTypeName(info.biometryType),
      };
    }

    if (info.code === BiometryErrorType.biometryNotEnrolled) {
      return {
        available: false,
        reason: 'notEnrolled',
        message: info.reason || 'No biometrics are enrolled on this device',
      };
    }

    return {
      available: false,
      reason: 'unavailable',
      message: info.reason || 'Biometrics are not available on this device',
    };
  } catch (error) {
    // checkBiometry() does not throw on a working install. If it does, the native
    // implementation is missing or broken — an integration bug, not a device
    // capability. Say so loudly instead of quietly hiding the feature.
    const message = error instanceof Error ? error.message : String(error);
    console.error('[Biometric] Plugin unavailable — native implementation missing?', error);
    return { available: false, reason: 'pluginUnavailable', message };
  }
}

/** Back-compat shape for existing callers. */
export async function checkBiometricAvailability(): Promise<BiometricAvailability> {
  const status = await getBiometricStatus();
  if (status.available) {
    return { isAvailable: true, biometryType: status.biometryType };
  }
  return {
    isAvailable: false,
    biometryType: BiometryType.none,
    errorMessage: status.message,
    pluginError: status.reason === 'pluginUnavailable' ? status.message : undefined,
  };
}

/**
 * Prompt for biometric authentication. Distinguishes cancellation from failure —
 * a user tapping Cancel is not an error and must not raise an error toast.
 */
export async function verifyBiometric(
  reason?: string
): Promise<{ verified: boolean; cancelled?: boolean; error?: string; code?: BiometryErrorType }> {
  try {
    await BiometricAuth.authenticate({
      reason: reason || 'Authenticate to sign in to Trade Loop',
      androidTitle: 'Trade Loop',
      androidSubtitle: 'Biometric Login',
      allowDeviceCredential: true,
      cancelTitle: 'Cancel',
      iosFallbackTitle: 'Use Passcode',
    });
    return { verified: true };
  } catch (error) {
    if (error instanceof BiometryError) {
      const cancelled =
        error.code === BiometryErrorType.userCancel ||
        error.code === BiometryErrorType.systemCancel ||
        error.code === BiometryErrorType.appCancel;
      return { verified: false, cancelled, error: error.message, code: error.code };
    }
    console.error('[Biometric] Authentication failed:', error);
    return {
      verified: false,
      error: error instanceof Error ? error.message : 'Authentication failed',
    };
  }
}

/** Save credentials for biometric login. */
export async function saveCredentials(
  email: string,
  password: string
): Promise<{ success: boolean; error?: string }> {
  try {
    await SecureStorage.set(CREDENTIALS_KEY, JSON.stringify({ username: email, password }));
    return { success: true };
  } catch (error) {
    console.error('[Biometric] Failed to save credentials:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to save credentials',
    };
  }
}

/** Read stored credentials. Returns null when none are stored. */
export async function getCredentials(): Promise<Credentials | null> {
  try {
    const stored = await SecureStorage.get(CREDENTIALS_KEY);
    if (typeof stored !== 'string' || !stored) return null;
    const parsed = JSON.parse(stored) as Credentials;
    return parsed?.username && parsed?.password ? parsed : null;
  } catch (error) {
    console.error('[Biometric] Failed to read credentials:', error);
    return null;
  }
}

/** Delete stored credentials. */
export async function deleteCredentials(): Promise<{ success: boolean; error?: string }> {
  try {
    await SecureStorage.remove(CREDENTIALS_KEY);
    return { success: true };
  } catch (error) {
    console.error('[Biometric] Failed to delete credentials:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to delete credentials',
    };
  }
}

/** Whether credentials are stored for biometric login. */
export async function hasStoredCredentials(): Promise<boolean> {
  return (await getCredentials()) !== null;
}

/** Verify identity, then return the stored credentials. */
export async function biometricLogin(): Promise<{
  success: boolean;
  credentials?: Credentials;
  cancelled?: boolean;
  error?: string;
}> {
  const { verified, cancelled, error: verifyError } = await verifyBiometric(
    'Authenticate to sign in to Trade Loop'
  );

  if (!verified) {
    return { success: false, cancelled, error: verifyError || 'Biometric verification failed' };
  }

  const credentials = await getCredentials();
  if (!credentials) {
    return { success: false, error: 'No stored credentials found' };
  }

  return { success: true, credentials };
}

export const biometricService = {
  checkAvailability: checkBiometricAvailability,
  getStatus: getBiometricStatus,
  getBiometryTypeName,
  verifyIdentity: verifyBiometric,
  saveCredentials,
  getCredentials,
  deleteCredentials,
  hasStoredCredentials,
  biometricLogin,
};

export default biometricService;
